import React from "react";

interface CustomButtonProps {
  onClick?: () => void;
  label?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  loading?: boolean;
  variant?: "primary" | "secondary" | "white" | "danger";
  size?: "small" | "medium" | "full" | string;
  className?: string;
  style?: React.CSSProperties;
}

const variantStyles: Record<string, string> = {
  primary: "bg-[#043A53] text-white hover:bg-[#065a8c]",
  secondary: "bg-blue-500 text-white hover:bg-blue-600",
  white: "bg-white text-[#043A53] border border-gray-300 hover:bg-gray-100",
  danger: "px-4 py-2 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20 font-medium transition-colors duration-200"
};

const sizeStyles: Record<string, string> = {
  small: "px-3 py-1 text-sm",
  medium: "px-5 py-2 text-base",
  full: "w-full px-5 py-2",
};

const disabledStyles = "bg-gray-300 text-gray-500 cursor-not-allowed border-none";

const CustomButton: React.FC<CustomButtonProps> = ({
  onClick,
  label,
  children,
  disabled = false,
  loading = false,
  variant = "primary",
  size = "medium",
  className = "",
  style,
}) => {
  // Determine variant and size classes
  const variantClass = variantStyles[variant] || variantStyles.primary;
  const sizeClass = typeof size === "string" && sizeStyles[size] ? sizeStyles[size] : "";
  const customSizeStyle: React.CSSProperties = {};
  if (size && !["small", "medium", "full"].includes(size)) {
    customSizeStyle.width = size;
  }
  const stateClass = disabled ? disabledStyles : variantClass;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className={`rounded-md font-semibold transition-all duration-200 flex items-center justify-center ${stateClass} ${sizeClass} ${className}`}
      style={{ ...style, ...customSizeStyle }}
    >
      {loading ? (
        <span className="loader mr-2 w-4 h-4 border-2 border-t-2 border-gray-200 border-t-blue-500 rounded-full animate-spin"></span>
      ) : null}
      {children || label}
    </button>
  );
};

export default CustomButton;